import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DevExtremeModule } from 'devextreme-angular';
import { IboxtoolsModule } from '../common/iboxtools/iboxtools.module';
import { baseZIndex } from 'devextreme/ui/overlay';
import { Options as dxDataGridOptions, default as dxDataGrid } from 'devextreme/ui/data_grid';
import { Options as dxTreeListOptions, default as dxTreeList } from 'devextreme/ui/tree_list';
import { Options as dxFormOptions, default as dxForm } from 'devextreme/ui/form';
import { Options as dxSelectBoxOptions, default as dxSelectBox } from 'devextreme/ui/select_box';
import { ModalModule } from 'ngx-bootstrap/modal';
import { RouterModule } from '@angular/router';
import { AuditDialogComponent } from './audit-dialog/audit-dialog.component';
import { InputDialogComponent } from './input-dialog/input-dialog.component';
import { SelectDialogComponent } from './select-dialog/select-dialog.component';
import { ListDialogComponent } from './list-dialog/list-dialog.component';
import { PartSelectDialogComponent } from './part-select-dialog/part-select-dialog.component';
import { PartTypeSelectDialogComponent } from './part-type-select-dialog/part-type-select-dialog.component';
import { PartTypesSelectDialogComponent } from './part-types-select-dialog/part-types-select-dialog.component';
import { PartPurchaseDialogComponent } from './part-purchase-dialog/part-purchase-dialog.component';
import { PartDialogComponent } from './part-dialog/part-dialog.component';
import { EmailDialogComponent } from './email-dialog/email-dialog.component';
import { CustomerAddDialogComponent } from './customer-add-dialog/customer-add-dialog.component';
import { NgxModalDraggableDirective } from './ngx-modal-draggable.directive';
import _ from 'lodash';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { ContactGridComponent } from './contact-grid/contact-grid.component';
import { ContactDialogComponent } from './contact-dialog/contact-dialog.component';
import { FormsModule } from '@angular/forms';

export { PartSelectDialogComponent, PartTypeSelectDialogComponent, PartTypesSelectDialogComponent };

@NgModule({
    imports: [CommonModule, FormsModule, ModalModule.forRoot(), RouterModule, DevExtremeModule, IboxtoolsModule],
    declarations: [
        AuditDialogComponent,
        InputDialogComponent,
        SelectDialogComponent,
        ListDialogComponent,
        PartSelectDialogComponent,
        PartTypeSelectDialogComponent,
        PartTypesSelectDialogComponent,
        PartPurchaseDialogComponent,
        CustomerAddDialogComponent,
        NgxModalDraggableDirective,
        PartDialogComponent,
        EmailDialogComponent,
        ContactGridComponent,
        ContactDialogComponent,
    ],
    exports: [
        DevExtremeModule,
        IboxtoolsModule,
        InputDialogComponent,
        SelectDialogComponent,
        ListDialogComponent,
        PartSelectDialogComponent,
        PartTypeSelectDialogComponent,
        PartTypesSelectDialogComponent,
        PartPurchaseDialogComponent,
        CustomerAddDialogComponent,
        NgxModalDraggableDirective,
        PartDialogComponent,
        EmailDialogComponent,
        ContactGridComponent,
    ],
})
export class SharedModule {
    public static onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.clearSelection();
        }
        const filterCheck = _.includes(e.fullName, 'filterValue');
        if (filterCheck) {
            e.component.clearSelection();
        }
    }

    public static onExporting(e) {
        SharedModule.onExportingGrid(e.component);
    }

    public static onExportingGrid(dataGrid: dxDataGrid, customizeCellOverride = null) {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Main sheet');
        exportDataGrid({
            component: dataGrid,
            worksheet: worksheet,
            customizeCell:
                customizeCellOverride ??
                function (options) {
                    options.excelCell.font = { name: 'Arial', size: 12 };
                    options.excelCell.alignment = { horizontal: 'left' };
                },
        }).then(function () {
            workbook.xlsx.writeBuffer().then(function (buffer: BlobPart) {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
            });
        });
    }

    constructor() {
        const dataGridOptions: dxDataGridOptions = {
            // height: '100%',
            height: '100px' /* hack default value to make scrolling work */,
            // width: '100px',
            width: '100%',
            columnAutoWidth: true,
            columnResizingMode: 'widget',
            allowColumnReordering: true,
            allowColumnResizing: true,
            hoverStateEnabled: false,
            showBorders: false,
            showColumnLines: false,
            showRowLines: true,
            paging: { enabled: false, pageSize: 50 },
            scrolling: { mode: 'virtual', showScrollbar: 'always', preloadEnabled: true },
            selection: { mode: 'single', showCheckBoxesMode: 'onClick' },
            stateStoring: { savingTimeout: 800 },
            filterRow: { applyFilter: 'auto' },
            focusedRowEnabled: false,
            columnChooser: { enabled: true, search: { enabled: true }, mode: 'select', height: 800, width: 300 },
            onContentReady: (e: any) => {
                var columnChooserView = e.component.getView('columnChooserView');
                if (!columnChooserView._popupContainer) {
                    columnChooserView._initializePopupContainer();
                    columnChooserView.render();
                    columnChooserView._popupContainer.option('position', { of: e.element, my: 'right top', at: 'right top', offset: '0 50' });
                }
            },
        };
        dxDataGrid.defaultOptions({ options: dataGridOptions });

        const treeListOptions: dxTreeListOptions = {
            // height: '100px',
            width: '100%',
            allowColumnResizing: true,
            columnResizingMode: 'widget',
            showBorders: true,
            hoverStateEnabled: false,
            selection: { mode: 'single' },
            stateStoring: { savingTimeout: 800 },
            scrolling: { mode: 'virtual', showScrollbar: 'always', preloadEnabled: true },
        };
        dxTreeList.defaultOptions({ options: treeListOptions });

        const formOptions: dxFormOptions = {
            showColonAfterLabel: false,
            labelLocation: 'left',
            // labelLocation: 'top'
        };
        dxForm.defaultOptions({ options: formOptions });

        const selectboxOptions: dxSelectBoxOptions = {
            searchEnabled: true,
            searchTimeout: 100,
            showClearButton: false,
            inputAttr: { autoComplete: false },
            onFocusIn: (e) => {
                const inputs = e.element.getElementsByTagName('input');
                inputs.item(inputs.length - 1).select();
            },
        };
        dxSelectBox.defaultOptions({ options: selectboxOptions });

        baseZIndex(3000);
    }
}
